import { useState, useEffect, useRef, useMemo } from "react";
import useAxios from "../../utils/useAxios"; // Import the custom Axios instance
import { formatIndianCurrency } from "../../utils/currencyFormat";
import { hideModal, showModal } from "../../utils/modalUtility";
import { currentDateAndTime } from "../../utils/generateCurrentDateAndTime";
import { downloadElementAsPdf } from "../../utils/downloadCardPdf";
import { useAgencies } from "../../hooks/useAgencies";
import DonorCard from "./DonorCard";
import PatronCardsSheet from "./PatronCardsSheet";

const CURRENT_YEAR = new Date().getFullYear();

const ALL_LEVELS = ["BRONZE", "SILVER", "GOLD", "DIAMOND"];

const MONTHS = [
  { name: "January", value: "01" },
  { name: "February", value: "02" },
  { name: "March", value: "03" },
  { name: "April", value: "04" },
  { name: "May", value: "05" },
  { name: "June", value: "06" },
  { name: "July", value: "07" },
  { name: "August", value: "08" },
  { name: "September", value: "09" },
  { name: "October", value: "10" },
  { name: "November", value: "11" },
  { name: "December", value: "12" },
];

const EMPTY_DATE_FILTERS = {
  from_date: "",
  to_date: "",
};

const EMPTY_FOLLOW_UP_DATA = {
  year: "",
  from_date: "",
  to_date: "",
  processing_agency: "",
};

const PATRON_CARDS_ENDPOINT = "/api/dashboard/download-all-patron-card";

function normalizePatronCard(patron) {
  return {
    full_name: patron?.full_name || "",
    scheme_patronship_level: patron?.scheme_patronship_level || "",
    patronship_no: patron?.patronship_no || "",
    patronship_level:
      patron?.patronship_level || patron?.scheme_patronship_level || "",
    profile_pic: patron?.profile_pic || null,
    date_of_birth: patron?.date_of_birth || "",
    status: patron?.status || "",
  };
}

function normalizePatronCardsResponse(payload) {
  if (!Array.isArray(payload?.patrons)) {
    return [];
  }

  return payload.patrons.map(normalizePatronCard);
}

async function fetchPatronCardsPage(axiosAPI, page) {
  const response = await axiosAPI.get(PATRON_CARDS_ENDPOINT, {
    params: { page },
  });

  return {
    cards: normalizePatronCardsResponse(response.data),
    currentPage: Number(response?.data?.currentPage) || Number(page) || 1,
    totalPages: Number(response?.data?.totalPages) || 1,
    totalRecords: Number(response?.data?.totalRecords) || 0,
  };
}

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [reportData, setReportData] = useState(null);
  const axiosAPI = useAxios(); // Use the custom Axios instance
  const [selectedMonth, setSelectedMonth] = useState("01");
  const [downloading, setDownloading] = useState(false);
  const [bookedCalendarLoading, setBookedCalendarLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [nonWant80gLoading, setNonWant80gLoading] = useState(false);

  const [uploadRows, setUploadRows] = useState([]);

  const [want80gLoading, setWant80gLoading] = useState(false);

  const [autoRecurringData, setAutoRecurringData] = useState({
    processing_agency: "",
    csv_file: null,
  });

  const { agencies, loading: agenciesLoading } = useAgencies();

  // console.log("Agencies", agencies);

  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [want80gSelectedYear, setWant80gSelectedYear] = useState(CURRENT_YEAR);
  const [showPatronCardsModal, setShowPatronCardsModal] = useState(false);
  const [patronCards, setPatronCards] = useState([]);
  const [patronCardsLoading, setPatronCardsLoading] = useState(false);
  const [patronPdfDownloading, setPatronPdfDownloading] = useState(false);
  const [patronCardsPage, setPatronCardsPage] = useState(1);
  const [patronCardsTotalPages, setPatronCardsTotalPages] = useState(1);
  const [patronCardsTotalRecords, setPatronCardsTotalRecords] = useState(0);

  const reportPdfRef = useRef();
  const patronCardsRef = useRef();
  const autoRecurringFileInputRef = useRef();

  const [filters, setFilters] = useState(EMPTY_DATE_FILTERS);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_DATE_FILTERS);
  };

  async function fetchGiftReport() {
    setDownloading(true); //  Start loader
    try {
      const response = await axiosAPI.get(
        `/api/dashboard/download-birthday-wishes-report?&from_date=${filters.from_date}&to_date=${filters.to_date}`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `donors.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      // alert("Failed to download file. Please try again later.");
      alert("No Data Found");
    } finally {
      setDownloading(false); // ⏹ End loader
    }
  }

  const fetchDashboardData = async () => {
    setLoading(true); // Set loading state to true before fetching data
    try {
      const response = await axiosAPI.get("/api/dashboard/dashboard-data");
      setDashboardData(response.data); // Set the fetched data to state
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false); // Set loading state to false after data is fetched
    }
  };

  const handleAutoRecurringSubmit = async (e) => {
    e.preventDefault();

    const { processing_agency, csv_file } = autoRecurringData;

    if (!processing_agency || !csv_file) {
      alert("Please select agency and upload CSV");
      return;
    }

    const formData = new FormData();
    formData.append("processing_agency", processing_agency);
    formData.append("excel", csv_file);
    formData.append("upload_date_and_time", currentDateAndTime());

    try {
      setIsUploading(true);
      const res = await axiosAPI.post(
        "/api/dashboard/upload-auto-recurring-donation",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      // FIXED HERE
      const {
        message = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = res.data.result || {};

      //  VERY IMPORTANT
      setUploadRows(rows);

      // Download failed excel
      if (failedExcelBase64) {
        const bytes = Uint8Array.from(atob(failedExcelBase64), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "auto_recurring_failed_rows.xlsx";
        link.click();
      }

      const failedRows = rows.filter((r) => r.status === "fail");

      if (rows.length === 0) {
        window.notifyMessage("Error", "No valid data found", "error");
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `Failed Rows: ${failedRows.length}`,
          "error",
        );
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `Inserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
      } else {
        window.notifyMessage("Success", "Upload successful", "success");
      }

      autoRecurringFileInputRef.current.value = "";
      setAutoRecurringData({
        processing_agency: "",
        csv_file: null,
      });
    } catch (error) {
      console.error(error);
      window.notifyMessage("Error", "Upload failed", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // console.log("Upload Rows:", uploadRows);

  // console.log("Dashboard Data",dashboardData)

  function getMonthName(value) {
    return MONTHS.find((m) => m.value === value)?.name || "";
  }

  // Ensure you are using empty array as fallback
  const {
    total_donors = 0,
    total_leads = 0,
    total_gifts = 0,
  } = dashboardData || {};

  const menu = useMemo(
    () => [
      {
        icon: <i className="fas fa-chalkboard-teacher"></i>,
        text: "Total Donors",
        value: total_donors,
        color: "icon-success",
      },
      {
        icon: <i className="fas fa-chalkboard-teacher"></i>,
        text: "Total Leads",
        value: total_leads,
        color: "icon-danger ",
        border: "border",
      },
      {
        icon: <i className="fas fa-folder-open"></i>,
        text: "Total Gifts",
        value: total_gifts,
        color: "ua-icon-gray",
      },
    ],
    [total_donors, total_leads, total_gifts],
  );

  const [followUpData, setFollowUpData] = useState(EMPTY_FOLLOW_UP_DATA);

  const downloadFollowUpMandareReportExcel = async (table_name, status) => {
    setDownloading(true); //  Start loader
    // const queryString = buildQuery(filters, "1");

    // console.log("query ", queryString);

    try {
      const response = await axiosAPI.get(
        `/api/donor/download-donor-excel?table=${table_name}&status=${status}&is_lead=yes`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); //  End loader
    }
  };

  const downloadWant80gExcel = async (table_name, status) => {
    setDownloading(true); //  Start loader
    // const queryString = buildQuery(filters, "1");

    // console.log("query ", queryString);

    try {
      const response = await axiosAPI.get(
        `/api/donor/download-want-80g-donor-excel`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `want_80g.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); // ⏹ End loader
    }
  };

  async function loadPatronCardsPage(page, shouldOpenModal = false) {
    try {
      setPatronCardsLoading(true);

      const pageData = await fetchPatronCardsPage(axiosAPI, page);
      const normalizedCards = pageData.cards;

      if (!normalizedCards.length) {
        alert("No patron cards found.");
        return;
      }

      setPatronCardsPage(pageData.currentPage);
      setPatronCardsTotalPages(pageData.totalPages);
      setPatronCardsTotalRecords(pageData.totalRecords);
      setPatronCards(normalizedCards);

      if (shouldOpenModal) {
        setShowPatronCardsModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch patron cards:", error);
      alert("Patron cards could not be loaded. Please try again later.");
    } finally {
      setPatronCardsLoading(false);
    }
  }

  async function downloadPatronCards(e) {
    e.preventDefault();
    await loadPatronCardsPage(1, true);
  }

  async function handlePatronCardsPageChange(nextPage) {
    const sanitizedPage = Math.min(
      Math.max(1, Number(nextPage) || 1),
      patronCardsTotalPages || 1,
    );

    if (sanitizedPage === patronCardsPage && patronCards.length) {
      return;
    }

    await loadPatronCardsPage(sanitizedPage);
  }

  function exportFollowUpMandateReport() {
    downloadFollowUpMandareReportExcel("donor", "rejected");
  }

  async function downloadBookedCalendarReport() {
    setBookedCalendarLoading(true);
    try {
      const response = await axiosAPI.get(
        "/api/donor/download-booked-calendar-donors",
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Booked_Calendar_Donors.xlsx");

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setBookedCalendarLoading(false); // End loader
    }
  }
  async function downloadWant80gReport() {
    const payload = {
      year: want80gSelectedYear,
    };

    // console.log("Payload", payload.year);

    // return;

    setWant80gLoading(true);
    try {
      const response = await axiosAPI.get(
        "/api/dashboard/download-wantg-report?year=" + payload.year,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Want_80g_report.xlsx");

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setWant80gLoading(false); // End loader
    }
  }

  async function downloadNonWant80gReport() {
    const payload = {
      year: want80gSelectedYear,
    };

    // console.log("Payload", payload.year);

    // return;

    setNonWant80gLoading(true);
    try {
      const response = await axiosAPI.get(
        "/api/dashboard/download-non80g-report?year=" + payload.year,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Non_Want_80g_report.xlsx");

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setNonWant80gLoading(false); // End loader
    }
  }

  const downloadFollowUpExcel = async (table_name, followUpData) => {
    setDownloading(true); // Start loader

    try {
      const params = new URLSearchParams({
        table: table_name,
        ...followUpData,
      }).toString();

      const response = await axiosAPI.get(
        `/api/recurring-donation/download-follow-up-excel?${params}`,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      hideModal("followUpModal");
      setFollowUpData(EMPTY_FOLLOW_UP_DATA);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); // End loader
    }
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();

    const { year, from_date, to_date, processing_agency } = followUpData;

    // console.log("Follow up", year, from_date, to_date, processing_agency);

    // return;

    if (!year || !from_date || !to_date) {
      alert("Please fill all the required fields.");
      return;
    }

    downloadFollowUpExcel("recurring_donations", followUpData); // You can change the export name

    // Optionally close modal
    // closeModal();
  };

  const downloadReportPdf = async () => {
    try {
      setDownloading(true);
      await downloadElementAsPdf(
        reportPdfRef.current,
        `Monthly_Donation_Report_${selectedMonth}_${selectedYear}.pdf`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const downloadPatronCardsPdf = async () => {
    try {
      setPatronPdfDownloading(true);

      if (!patronCards.length) {
        alert("Please load patron cards first.");
        return;
      }

      await downloadElementAsPdf(
        patronCardsRef.current,
        `Patron_Cards_Page_${patronCardsPage}_${patronCards.length}.pdf`,
      );
    } finally {
      setPatronPdfDownloading(false);
    }
  };

  const closePatronCardsModal = () => {
    setShowPatronCardsModal(false);
    setPatronCards([]);
    setPatronCardsPage(1);
  };

  // Fetch dashboard data when the component mounts
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleGenerate = async () => {
    // console.log(`Selected: ${selectedMonth} ${selectedYear}`);

    setLoading(true); // Set loading state to true before fetching data
    try {
      const response = await axiosAPI.get(
        `/api/dashboard/get-donation-report?month=${selectedMonth}&year=${selectedYear}`,
      );
      // console.log("reponse", response.data);
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false); // Set loading state to false after data is fetched
    }
  };

  const apiData = reportData?.level;

  const levelDataMap = useMemo(() => {
    const mappedData = {};

    // apiData?.forEach((item) => {
    //   mappedData[item.patronship_level] = item.total_count;
    // });

    apiData?.forEach((item) => {
      mappedData[item.patronship_level] = {
        active: Number(item.active_count) || 0,
        onHold: Number(item.on_hold_count) || 0,
        closed: Number(item.closed_count) || 0,
      };
    });

    return mappedData;
  }, [apiData]);

  const reportYears = useMemo(
    () =>
      Array.from({ length: CURRENT_YEAR - 2020 }, (_, i) => {
        const year = 2021 + i;
        return year;
      }),
    [],
  );

  function handleResetFollowUpFilters() {
    setFollowUpData(EMPTY_FOLLOW_UP_DATA);
  }

  // Show Bootstrap spinner while loading
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-inner">
        <div className="row">
          {/* Online Screens Card */}
          {menu.map((item, index) => {
            return (
              <div className="col-sm-6 col-md-3">
                <div className="card card-stats card-round">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-icon">
                        <div
                          className={`icon-big text-center ${item.color} ${item.border} bubble-shadow-small`}
                        >
                          {/* <i className="fas fa-chalkboard-teacher"></i> */}
                          {item.icon}
                        </div>
                      </div>
                      <div className="col col-stats ms-3 ms-sm-0">
                        <div className="numbers">
                          {/* <p className="card-category">Total Donors</p> */}
                          <p className="card-category">{item.text}</p>
                          <h4 className="card-title">{item.value}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Birthday Report Card */}
          <div className="sm:page-inner">
            <div className="row">
              <div className="col-12 col-md-6 mb-4">
                <div
                  className="card h-100"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading inside the card */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-birthday-cake text-danger fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Birthday Report
                      </h5>
                    </div>

                    {/* Filter Form */}
                    <div className="d-flex flex-wrap align-items-end gap-3">
                      {/* From Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="fromDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>{" "}
                          From Date
                        </label>
                        <input
                          id="fromDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={filters.from_date || ""}
                          onChange={(e) =>
                            handleFilterChange("from_date", e.target.value)
                          }
                        />
                      </div>

                      {/* To Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="toDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>{" "}
                          To Date
                        </label>
                        <input
                          id="toDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={filters.to_date || ""}
                          onChange={(e) =>
                            handleFilterChange("to_date", e.target.value)
                          }
                        />
                      </div>

                      {/* Export Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={() => {
                            if (filters.from_date && filters.to_date) {
                              fetchGiftReport();
                            } else {
                              alert("Please select both From and To dates.");
                            }
                          }}
                        >
                          <i className="fas fa-file-export me-2"></i> Export
                        </button>
                      </div>

                      {/* Reset Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: "38px", height: "38px" }}
                          title="Reset Filters"
                          onClick={handleResetFilters}
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="card mb-4 shadow-sm">
                  <div className="card-body">
                    {/* 🔹 Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <i className="fas fa-hand-holding-heart text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Export Follow-Up Transaction Report
                      </h5>
                    </div>

                    {/* 🔹 Form Starts */}
                    <form
                      onSubmit={handleFollowUpSubmit}
                      className="d-flex flex-wrap align-items-end gap-3"
                    >
                      {/* 🔸 Year */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="yearSelect"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar me-1 text-primary"></i>
                          Year
                        </label>
                        <select
                          id="yearSelect"
                          className="form-select shadow-sm border"
                          style={{ minWidth: "120px" }}
                          value={followUpData.year}
                          onChange={(e) =>
                            setFollowUpData({
                              ...followUpData,
                              year: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Year --</option>
                          {Array.from({ length: 10 }, (_, i) => {
                            const year = CURRENT_YEAR - i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* 🔸 From Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="fromDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          From Date
                        </label>
                        <input
                          id="fromDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={followUpData.from_date || ""}
                          onChange={(e) =>
                            setFollowUpData({
                              ...followUpData,
                              from_date: e.target.value,
                            })
                          }
                        />
                      </div>

                      {/* 🔸 To Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="toDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          To Date
                        </label>
                        <input
                          id="toDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={followUpData.to_date || ""}
                          onChange={(e) =>
                            setFollowUpData({
                              ...followUpData,
                              to_date: e.target.value,
                            })
                          }
                        />
                      </div>
                      {/* Processing Agency */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="toDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          Processing Agency
                        </label>
                        <select
                          className="form-select"
                          aria-label="Default select example"
                          value={followUpData.processing_agency}
                          onChange={(e) =>
                            setFollowUpData({
                              ...followUpData,
                              processing_agency: e.target.value,
                            })
                          }
                        >
                          <option selected disabled>
                            ---Select Agency---
                          </option>
                          {/* <option value="YES BANK">YES BANK</option>
                          <option value="TECH PROCESS">TECH PROCESS</option>
                          <option value="SBI ACH">SBI ACH</option>
                          <option value="SBI SI">SBI SI</option>
                          <option value="PHONE PAY">PHONE PAY</option>
                          <option value="RAZORPAY">RAZORPAY</option>
                          <option value="TP3489">TP3489</option>
                          <option value="EASE BUZZ">EASE BUZZ</option> */}
                          {!agenciesLoading &&
                            agencies.map((agency) => (
                              <option key={agency.name} value={agency.name}>
                                {agency.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* 🔹 Export Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          type="submit"
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                        >
                          <i className="fas fa-file-export me-2"></i> Export
                        </button>
                      </div>

                      {/* 🔹 Reset Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          type="button"
                          className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: "38px", height: "38px" }}
                          title="Reset Filters"
                          onClick={handleResetFollowUpFilters}
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      </div>
                    </form>
                    {/* 🔹 Form Ends */}
                  </div>
                </div>
              </div>

              {/* Export Follow Up Mandate Report Card */}
              <div className="col-12 col-md-6">
                <div
                  className="card mb-4"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-hand-holding-heart text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Export Follow Up Mandate Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-1">
                      {/* Month Selector */}
                      {/* <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          Month
                        </label>
                        <select
                          className="form-select shadow-sm border"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                          {MONTHS.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.name}
                            </option>
                          ))}
                        </select>
                      </div> */}

                      {/* Year Selector */}
                      {/* <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar me-1 text-primary"></i>{" "}
                          Year
                        </label>
                        <select
                          className="form-select shadow-sm border"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                        >
                          {Array.from(
                            { length: new Date().getFullYear() - 2020 },
                            (_, i) => {
                              const year = 2021 + i;
                              return (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              );
                            }
                          )}
                        </select>
                      </div> */}

                      {/* Generate Button */}
                      {/* <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-primary rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={handleGenerate}
                        >
                          <i className="fas fa-sync me-2"></i> Generate
                        </button>
                      </div> */}

                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={exportFollowUpMandateReport}
                        >
                          <i className="fas fa-print me-2"></i> Export
                        </button>
                      </div>
                      {/* <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={downloadWant80gExcel}
                        >
                          <i className="fas fa-print me-2"></i> Export Want 80G
                        </button>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
              {/* Export Follow Up Mandate Report Card */}
              <div className="col-12 col-md-6">
                <div
                  className="card mb-4"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-chart-line text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Export Booked Calendar Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-1">
                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          disabled={bookedCalendarLoading}
                          onClick={downloadBookedCalendarReport}
                        >
                          <i className="fas fa-print me-2"></i> Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Follow Up Mandate Report Card */}
              <div className="col-12 col-md-6">
                <div
                  className="card mb-4"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-chart-line text-success fs-4"></i>

                      <h5 className="mb-0 fw-bold text-dark">
                        Export Want 80g Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-2">
                      {/* Year Dropdown */}
                      <div>
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar me-1 text-primary"></i>{" "}
                          Selected Year
                        </label>
                        <select
                          className="form-select"
                          style={{ height: "38px" }}
                          value={want80gSelectedYear}
                          onChange={(e) =>
                            setWant80gSelectedYear(e.target.value)
                          }
                        >
                          {[2026, 2025, 2024, 2023, 2022].map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          disabled={want80gLoading}
                          onClick={downloadWant80gReport}
                        >
                          <i className="fas fa-print me-2"></i> Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Non Want 80g Report Card */}
              <div className="col-12 col-md-6">
                <div
                  className="card mb-4"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-chart-line text-success fs-4"></i>

                      <h5 className="mb-0 fw-bold text-dark">
                        Export Non Want 80g Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-2">
                      {/* Year Dropdown */}
                      <div>
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar me-1 text-primary"></i>{" "}
                          Selected Year
                        </label>
                        <select
                          className="form-select"
                          style={{ height: "38px" }}
                          value={want80gSelectedYear}
                          onChange={(e) =>
                            setWant80gSelectedYear(e.target.value)
                          }
                        >
                          {[2026, 2025, 2024, 2023, 2022].map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          disabled={nonWant80gLoading}
                          onClick={downloadNonWant80gReport}
                        >
                          <i className="fas fa-print me-2"></i> Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Donation Report Card */}
              <div className="col-sm-12 col-md-6 mb-4">
                <div
                  className="card h-100"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-hand-holding-heart text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Monthly Donation Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-3">
                      {/* Month Selector */}
                      <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          Month
                        </label>
                        <select
                          className="form-select shadow-sm border"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                          {MONTHS.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Year Selector */}
                      <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar me-1 text-primary"></i>{" "}
                          Year
                        </label>
                        <select
                          className="form-select shadow-sm border"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                        >
                          {reportYears.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Generate Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-primary rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={handleGenerate}
                        >
                          <i className="fas fa-sync me-2"></i> Generate
                        </button>
                      </div>

                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={downloadReportPdf}
                        >
                          <i className="fas fa-print me-2"></i> Print
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload Auto Recurring Donation Using csv */}
              <div className="col-12 col-md-6">
                <div className="card mb-4 shadow-sm">
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <i className="fas fa-hand-holding-heart text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Upload Auto Recurring Donation
                      </h5>
                    </div>

                    {/* Form Starts */}
                    <form
                      onSubmit={handleAutoRecurringSubmit}
                      className="d-flex flex-wrap align-items-end gap-3"
                    >
                      {/* Processing Agency */}
                      <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>
                          Processing Agency
                        </label>

                        <select
                          className="form-select"
                          disabled={isUploading}
                          aria-label="Default select example"
                          value={autoRecurringData.processing_agency}
                          onChange={(e) =>
                            setAutoRecurringData({
                              ...autoRecurringData,
                              processing_agency: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="" disabled>
                            ---Select Agency---
                          </option>
                          {/* <option value="YES BANK">YES BANK</option>
                          <option value="TECH PROCESS">TECH PROCESS</option>
                          <option value="SBI ACH">SBI ACH</option>
                          <option value="SBI SI">SBI SI</option>
                          <option value="PHONE PAY">PHONE PAY</option>
                          <option value="RAZORPAY">RAZORPAY</option>
                          <option value="EASE BUZZ">EASE BUZZ</option> */}
                          {!agenciesLoading &&
                            agencies.map((agency) => (
                              <option key={agency.name} value={agency.name}>
                                {agency.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* CSV Upload */}
                      <div className="d-flex flex-column">
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-file-csv me-1 text-success"></i>
                          Upload xlsx
                        </label>

                        <input
                          type="file"
                          ref={autoRecurringFileInputRef}
                          disabled={isUploading}
                          className="form-control"
                          accept=".csv,.xlsx"
                          onChange={(e) =>
                            setAutoRecurringData({
                              ...autoRecurringData,
                              csv_file: e.target.files[0],
                            })
                          }
                          required
                        />
                      </div>

                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href =
                              "/samples/All response sample sheet.rar";
                            link.download = "All response sample sheet.rar";
                            link.click();
                          }}
                        >
                          Download Sample Sheet
                        </button>
                      </div>

                      {/* Submit */}
                      <div>
                        <button
                          type="submit"
                          disabled={patronCardsLoading}
                          className="btn btn-success btn-round"
                        >
                          {patronCardsLoading ? "Loading..." : "Submit"}
                        </button>
                      </div>
                    </form>
                    {/* {uploadRows.length > 0 && (
                        <table
                          border="1"
                          cellPadding="8"
                          style={{ marginTop: "20px" }}
                        >
                          <thead>
                            <tr>
                              <th>Row No</th>
                              <th>Status</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadRows.map((row, index) => (
                              <tr key={index}>
                                <td>{row.row}</td>
                                <td
                                  style={{
                                    color:
                                      row.status === "fail" ? "red" : "green",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {row.status}
                                </td>
                                <td>{row.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )} */}
                    {/* Form Ends */}
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card mb-4 shadow-sm">
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <i className="fas fa-download text-success fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Download Patron Cards
                      </h5>
                    </div>

                    {/* Form Starts */}
                    <form onSubmit={downloadPatronCards}>
                      <p className="text-muted mb-3">
                        Clicking the button will open the first 25 patron cards
                        in preview. From there, you can download the PDF or
                        switch to another page batch.
                      </p>
                      <button
                        type="submit"
                        disabled={patronCardsLoading}
                        className="btn btn-success btn-round px-4"
                      >
                        {patronCardsLoading ? "Loading..." : "Download Cards"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pdf Modal */}
          {showPatronCardsModal && (
            <div
              className="modal show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div
                className="modal-dialog modal-xl"
                style={{ maxWidth: "960px" }}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <div className="d-flex flex-column gap-2 w-100 pe-3">
                      <h5 className="modal-title mb-0">
                        Patron Cards Preview - Page {patronCardsPage} of{" "}
                        {patronCardsTotalPages} ({patronCards.length})
                      </h5>
                      <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={
                              patronCardsLoading || patronCardsPage <= 1
                            }
                            onClick={() =>
                              handlePatronCardsPageChange(patronCardsPage - 1)
                            }
                          >
                            Previous
                          </button>

                          <label className="fw-semibold text-muted small mb-0">
                            Page Batch
                          </label>

                          <select
                            className="form-select form-select-sm"
                            style={{ width: "110px" }}
                            value={patronCardsPage}
                            disabled={patronCardsLoading}
                            onChange={(e) =>
                              handlePatronCardsPageChange(e.target.value)
                            }
                          >
                            {Array.from(
                              { length: patronCardsTotalPages || 1 },
                              (_, index) => index + 1,
                            ).map((page) => (
                              <option key={page} value={page}>
                                {`Page ${page}`}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={
                              patronCardsLoading ||
                              patronCardsPage >= patronCardsTotalPages
                            }
                            onClick={() =>
                              handlePatronCardsPageChange(patronCardsPage + 1)
                            }
                          >
                            Next
                          </button>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                            {`Total Pages: ${patronCardsTotalPages}`}
                          </span>
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2">
                            {`Total Records: ${patronCardsTotalRecords || 0}`}
                          </span>
                          <span className="badge bg-light text-dark border px-3 py-2">
                            25 cards per batch
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closePatronCardsModal}
                    ></button>
                  </div>

                  <div
                    className="modal-body bg-light"
                    style={{
                      maxHeight: "calc(100vh - 210px)",
                      overflowY: "auto",
                    }}
                  >
                    <PatronCardsSheet
                      ref={patronCardsRef}
                      cards={patronCards}
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-success"
                      onClick={downloadPatronCardsPdf}
                      disabled={patronPdfDownloading}
                    >
                      {patronPdfDownloading ? "Downloading..." : "Download PDF"}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={closePatronCardsModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {reportData && (
            <div className="table-responsive mt-4" ref={reportPdfRef}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-white"
                    >
                      {/* {`Monthly Report For $ ${selectedYear}`} */}
                      Monthly Report For {getMonthName(selectedMonth)}{" "}
                      {selectedYear}
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Total Collection
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {[
                    {
                      label: "Online Registration",
                      value: reportData.online_registration || 0,
                    },
                    {
                      label: "Counter Registration ECS",
                      value: reportData.counter_registration_ecs || 0,
                    },
                    // {
                    //   label: "Cash Receipt",
                    //   value: reportData.cash_receipt || 0,
                    // },
                    {
                      label: "Cheque Amount",
                      value: reportData?.total_cheque_amount || 0,
                    },
                    {
                      label: "Online Amount",
                      value: reportData?.total_online_amount || 0,
                    },
                    {
                      label: "Cash Amount",
                      value: reportData?.total_cash_amount || 0,
                    },
                    {
                      label: "Total Monthly Donors",
                      value: reportData?.total_monthly_donors || 0,
                      isCount: true,
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-medium">{row.label}</td>
                      <td colSpan="2" className="text-center">
                        {row.isCount ? row.value : formatIndianCurrency(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Monthly Report For April 2025
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Mandate Report
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {/* <tr>
                    <td className="fw-medium">Overall Active Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.users?.find(
                        (item) => item.status === "active",
                      )?.total_count ?? 0}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Overall ON HOLD Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.users?.find(
                        (item) => item.status === "on-hold",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Overall Closed Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.users?.find(
                        (item) => item.status === "closed",
                      )?.total_count ?? "0"}
                    </td>
                  </tr> */}
                  <tr>
                    <td className="fw-medium">Mandates Sent</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.cash_receipt ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Newly Active Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.online_amount ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Rejected Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.users?.find(
                        (item) => item.status === "rejected",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-medium">ONE TIME</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.total_donations[0]?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TOTAL RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {/* {reportData?.donations
                        .reduce(
                          (acc, itr) => acc + Number(itr.total_amount || 0),
                          0
                        )
                        .toLocaleString() ?? "0"
                        
                        } */}
                      {formatIndianCurrency(
                        (reportData?.donations || []).reduce(
                          (acc, itr) => acc + Number(itr.total_amount || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">YES BANK RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "YES BANK",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TECH PROCESS RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "TECH PROCESS",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TP3489 RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "TP3489",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">RAZORPAY RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "RAZORPAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">PHONEPAY RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "PHONE PAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SBI SI</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "SBI SI",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SBI ACH</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "SBI ACH",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">EASE BUZZ</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "EASE BUZZ",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Overall Rejected Recurring Transactions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-medium">Processing Agency</td>
                    <td className="fw-medium">Amount</td>
                    <td className="fw-medium">Count</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">YES BANK</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "YES BANK",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "YES BANK",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TECH PROCESS</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "TECH PROCESS",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "TECH PROCESS",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">RAZORPAY</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "RAZORPAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "RAZORPAY",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Active Patrons Reports
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Total Active Patrons Summary
                    </th>
                  </tr>
                  <tr>
                    <th className="fw-medium">Category</th>
                    <th className="fw-medium text-center">Active</th>
                    <th className="fw-medium text-center">On-Hold / Closed</th>
                  </tr>
                </thead>

                <tbody>
                  {ALL_LEVELS.map((level, index) => {
                    const data = levelDataMap[level] || {};

                    return (
                      <tr key={index}>
                        <td className="fw-medium">{level}</td>
                        <td className="text-center">{data.active || 0}</td>
                        <td className="text-center">
                          {data.onHold || 0} / {data.closed || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Active Patrons Reports
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Overall Information
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
